import { For, Show, createSignal, createEffect, onMount, onCleanup } from 'solid-js';
import type { Game, Bet } from '../../game/Game';
import { RoundState } from '../../game/Game';
import { DEFAULT_USERNAME, DEFAULT_AVATAR, avatarSrc, MIN_CASHOUT_MULTIPLIER } from '../../game/constants';
import { formatCurrency } from '../../utils/format';
import { gameApi, isRealMode } from '../../api';
import { gameStore } from '../../store/gameStore';

type ViewMode = 'summary' | 'my-history' | 'round-bets' | 'top-players';

type BetState = 'pending' | 'queued' | 'active' | 'cashed' | 'lost' | 'won' | 'canceled' | 'refunded' | 'unknown';

interface TrackedBet {
  id: string;
  slotId: number;
  amount: number;
  state: BetState;
  cashoutMultiplier?: number;
  auto?: boolean;
  username?: string;
  avatar?: string;
  winAmount?: number;
  timestamp: number;
  roundNumber: number;
}

interface PlayerSummary {
  totalBets: number;
  totalWins: number;
  winRate: number;
  totalWagered: number;
  totalWon: number;
  netProfit: number;
  highestCashout: number;
  averageMultiplier: number;
}

interface RoundSummary {
  roundNumber: number;
  totalPlayers: number;
  totalBets: number;
  totalVolume: number;
  largestBet: number;
}

interface TopPlayer {
  username: string;
  avatar: string;
  value: number;
  label: string;
  rank: number;
}

export default function StatsDrawer() {
  const [isOpen, setIsOpen] = createSignal(false);
  const [viewMode, setViewMode] = createSignal<ViewMode>('summary');

  const [myBets, setMyBets] = createSignal<TrackedBet[]>([]);
  const [roundBets, setRoundBets] = createSignal<TrackedBet[]>([]);
  const [playerSummary, setPlayerSummary] = createSignal<PlayerSummary>({
    totalBets: 0,
    totalWins: 0,
    winRate: 0,
    totalWagered: 0,
    totalWon: 0,
    netProfit: 0,
    highestCashout: 0,
    averageMultiplier: 0,
  });
  const [roundSummary, setRoundSummary] = createSignal<RoundSummary>({
    roundNumber: 1,
    totalPlayers: 0,
    totalBets: 0,
    totalVolume: 0,
    largestBet: 0,
  });
  const [topPlayers, setTopPlayers] = createSignal<TopPlayer[]>(isRealMode
    ? []
    : [
      { username: 'CryptoWhale🐋', avatar: 'tiger', value: 125847.50, label: 'earned', rank: 1 },
      { username: 'MoonBoi', avatar: 'monkey', value: 98234.25, label: 'earned', rank: 2 },
      { username: 'DiamondHands💎', avatar: 'pilot', value: 76543.10, label: 'earned', rank: 3 },
      { username: 'RocketRider🚀', avatar: 'gamer', value: 54321.80, label: 'earned', rank: 4 },
      { username: 'LuckyPepe', avatar: 'hustler', value: 43210.50, label: 'earned', rank: 5 },
    ]);
  const [summaryLoading, setSummaryLoading] = createSignal(isRealMode);
  const [historyLoading, setHistoryLoading] = createSignal(isRealMode);
  const [leaderboardLoading, setLeaderboardLoading] = createSignal(isRealMode);

  let game: Game | undefined;

  const updatePlayerSummary = () => {
    const bets = myBets();
    const totalBets = bets.length;
    const resolved = bets.filter(b => b.state === 'won' || b.state === 'cashed' || b.state === 'lost');
    const wins = resolved.filter(b => b.state === 'won' || b.state === 'cashed').length;
    const winRate = resolved.length > 0 ? (wins / resolved.length) * 100 : 0;
    const totalWagered = bets.reduce((sum, b) => sum + b.amount, 0);
    const totalWon = bets.reduce((sum, b) => sum + (b.winAmount || 0), 0);
    const netProfit = totalWon - totalWagered;

    const cashedBets = bets.filter(b => b.cashoutMultiplier);
    const highestCashout = cashedBets.length > 0
      ? Math.max(...cashedBets.map(b => b.cashoutMultiplier || 0))
      : 0;
    const averageMultiplier = cashedBets.length > 0
      ? cashedBets.reduce((sum, b) => sum + (b.cashoutMultiplier || 0), 0) / cashedBets.length
      : 0;

    setPlayerSummary({
      totalBets,
      totalWins: wins,
      winRate,
      totalWagered,
      totalWon,
      netProfit,
      highestCashout,
      averageMultiplier,
    });
  };

  const updateRoundSummary = () => {
    const bets = roundBets();
    const uniquePlayers = new Set(bets.map(b => b.username || DEFAULT_USERNAME)).size;
    const totalVolume = bets.reduce((sum, b) => sum + b.amount, 0);
    const largestBet = bets.length > 0 ? Math.max(...bets.map(b => b.amount)) : 0;

    setRoundSummary(prev => ({
      ...prev,
      totalPlayers: uniquePlayers,
      totalBets: bets.length,
      totalVolume,
      largestBet,
    }));
  };

  const attachToGame = () => {
    const app = (window as any).memePumpApp as { getGame: () => Game } | undefined;
    game = app?.getGame();
    if (!game) return;

    const hydrateFromGame = () => {
      try {
        const history = (game as any).getRoundHistory?.() || [];
        const current = (game as any).getCurrentRound?.() || null;
        const roundNumber = (history?.length || 0) + 1;

          const map = new Map<string, TrackedBet>();

        const addFromRound = (round: any, isCurrent: boolean) => {
          if (!round || !Array.isArray(round.bets)) return;
          for (const b of round.bets) {
            const hasCash = !!b.cashoutMultiplier && b.cashoutMultiplier >= MIN_CASHOUT_MULTIPLIER;
            let state: BetState;
            if (hasCash) state = 'cashed';
            else if (isCurrent) {
              const st = (game as any).getCurrentState?.() || RoundState.BET_TIME;
              state = b.isActive ? (st === RoundState.BET_TIME ? 'pending' : 'active') : 'lost';
            } else {
              state = 'lost';
            }
            const tracked: TrackedBet = {
              id: b.id,
              slotId: b.slotId,
              amount: b.amount,
              state,
              cashoutMultiplier: b.cashoutMultiplier,
              username: b.username,
              avatar: b.avatar,
              winAmount: hasCash ? (b.amount * b.cashoutMultiplier) : undefined,
              timestamp: b.timestamp || Date.now(),
              roundNumber,
            };
            map.set(tracked.id, tracked);
          }
        };

        for (let i = history.length - 1; i >= 0; i--) addFromRound(history[i], false);
        if (current) addFromRound(current, true);

        const list = Array.from(map.values()).sort((a, b) => b.timestamp - a.timestamp);
        setMyBets(list);

        if (current) {
          const curIds = new Set(current.bets.map((b: any) => b.id));
          setRoundBets(list.filter(b => curIds.has(b.id)));
        } else {
          setRoundBets([]);
        }

        setRoundSummary(prev => ({
          ...prev,
          roundNumber,
          totalPlayers: current ? new Set(current.bets.map((b: any) => b.username || DEFAULT_USERNAME)).size : 0,
          totalBets: current ? current.bets.length : 0,
          totalVolume: current ? current.bets.reduce((s: number, b: any) => s + b.amount, 0) : 0,
          largestBet: current && current.bets.length ? Math.max(...current.bets.map((b: any) => b.amount)) : 0,
        }));
        updatePlayerSummary();
      } catch {}
    };

    game.on('roundStart', (e: any) => {
      setRoundBets([]);
      setRoundSummary(prev => ({
        ...prev,
        roundNumber: prev.roundNumber + 1,
        totalPlayers: 0,
        totalBets: 0,
        totalVolume: 0,
        largestBet: 0,
      }));
    });

    game.on('betPlaced', (e: any) => {
      const bet: Bet = e.data.bet;
      const tracked: TrackedBet = {
        id: bet.id,
        slotId: bet.slotId,
        amount: bet.amount,
        state: 'pending',
        username: bet.username,
        avatar: bet.avatar,
        timestamp: Date.now(),
        roundNumber: roundSummary().roundNumber,
      };

      setMyBets(prev => [tracked, ...prev]);
      setRoundBets(prev => [tracked, ...prev]);
      updatePlayerSummary();
      updateRoundSummary();
    });

    game.on('betActivated', (e: any) => {
      const bet: Bet = e.data.bet;
      setMyBets(prev => prev.map(b => b.id === bet.id ? { ...b, state: 'active' as BetState } : b));
      setRoundBets(prev => prev.map(b => b.id === bet.id ? { ...b, state: 'active' as BetState } : b));
    });

    game.on('cashout', (e: any) => {
      const bet: Bet = e.data.bet;
      const winAmount = e.data.winAmount || (bet.amount * (e.data.multiplier || 1));

      setMyBets(prev => prev.map(b =>
        b.id === bet.id
          ? { ...b, state: 'cashed' as BetState, cashoutMultiplier: e.data.multiplier, winAmount, auto: e.data.auto }
          : b
      ));
      setRoundBets(prev => prev.map(b =>
        b.id === bet.id
          ? { ...b, state: 'cashed' as BetState, cashoutMultiplier: e.data.multiplier, winAmount, auto: e.data.auto }
          : b
      ));
      updatePlayerSummary();
    });

    game.on('betWin', (e: any) => {
      const bet: Bet = e.data.bet;
      const winAmount = e.data.winAmount || (bet.amount * (bet.cashoutMultiplier || 1));

      setMyBets(prev => prev.map(b =>
        b.id === bet.id && b.state !== 'cashed'
          ? { ...b, state: 'won' as BetState, cashoutMultiplier: bet.cashoutMultiplier, winAmount }
          : b
      ));
      setRoundBets(prev => prev.map(b =>
        b.id === bet.id && b.state !== 'cashed'
          ? { ...b, state: 'won' as BetState, cashoutMultiplier: bet.cashoutMultiplier, winAmount }
          : b
      ));
      updatePlayerSummary();
    });

    game.on('betLoss', (e: any) => {
      const bet: Bet = e.data.bet;
      setMyBets(prev => prev.map(b => b.id === bet.id ? { ...b, state: 'lost' as BetState } : b));
      setRoundBets(prev => prev.map(b => b.id === bet.id ? { ...b, state: 'lost' as BetState } : b));
      updatePlayerSummary();
    });

    hydrateFromGame();
  };

  const toggleDrawer = (open?: boolean) => {
    const nextState = open !== undefined ? open : !isOpen();
    setIsOpen(nextState);

    // In real mode, request fresh data when drawer opens
    if (nextState && isRealMode) {
      setSummaryLoading(true);
      setHistoryLoading(true);
      setLeaderboardLoading(true);
      gameApi.getPlayerStats();
      gameApi.getBetHistory({ limit: 50 });
      gameApi.getLeaderboard({ type: 'earnings', limit: 10 });
    }

    try {
      const app = (window as any).memePumpApp;
      app?.audio?.play('ui-toggle', { allowRestart: true });
    } catch {}
  };

  const closeDrawer = () => toggleDrawer(false);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen()) {
      e.preventDefault();
      closeDrawer();
    }
  };

  const handleToggleEvent = (e: CustomEvent) => {
    toggleDrawer(e.detail?.open);
  };

  onMount(() => {
    // ── Real mode: populate from server responses via gameStore ──
    if (isRealMode) {
      // Player stats → playerSummary
      createEffect(() => {
        const stats = gameStore.playerStats();
        if (!stats) return;
        setPlayerSummary({
          totalBets: stats.totalBets,
          totalWins: stats.totalWins,
          winRate: stats.winRate,
          totalWagered: stats.totalWagered,
          totalWon: stats.totalWon,
          netProfit: stats.netProfit,
          highestCashout: stats.highestCashout,
          averageMultiplier: stats.averageMultiplier,
        });
        setSummaryLoading(false);
      });

      // Bet history → myBets
      createEffect(() => {
        const data = gameStore.betHistory();
        if (!data) return;
        const mapped: TrackedBet[] = data.bets.map(b => ({
          id: String(b.betId),
          slotId: b.slotId,
          amount: b.amount,
          state: normalizeBetState((b.state || b.result) as string),
          cashoutMultiplier: b.cashoutMultiplier ?? undefined,
          winAmount: b.winAmount ?? undefined,
          timestamp: new Date(b.timestamp).getTime(),
          roundNumber: b.roundId,
        }));
        setMyBets(mapped);
        setHistoryLoading(false);
      });

      // Leaderboard → topPlayers
      createEffect(() => {
        const data = gameStore.leaderboard();
        if (!data) return;
        setTopPlayers(data.entries.map(e => ({
          username: e.username,
          avatar: e.avatar || DEFAULT_AVATAR,
          value: e.value,
          label: e.label,
          rank: e.rank,
        })));
        setLeaderboardLoading(false);
      });

      // Round bets → roundBets + roundSummary
      createEffect(() => {
        const bets = gameStore.roundBets();
        const rn = gameStore.roundNumber();
        const mapped: TrackedBet[] = bets.map(b => ({
          id: b.betId,
          slotId: b.slotId,
          amount: b.amount,
          state: (() => {
            if (b.cashoutMultiplier) return 'cashed' as BetState;
            const st = String(b.state || '').toUpperCase();
            if (st === 'ACTIVE') return 'active' as BetState;
            if (st === 'PENDING') return 'pending' as BetState;
            if (st === 'QUEUED') return 'queued' as BetState;
            if (st === 'LOST') return 'lost' as BetState;
            if (st === 'WON') return 'won' as BetState;
            if (st === 'CASHED') return 'cashed' as BetState;
            if (st === 'CANCELED') return 'canceled' as BetState;
            if (st === 'REFUNDED') return 'refunded' as BetState;
            return 'unknown' as BetState;
          })(),
          cashoutMultiplier: b.cashoutMultiplier ?? undefined,
          winAmount: b.winAmount ?? undefined,
          username: b.username,
          avatar: b.avatar ?? undefined,
          timestamp: Date.now(),
          roundNumber: rn,
        }));
        setRoundBets(mapped);

        const uniquePlayers = new Set(bets.map(b => b.username || DEFAULT_USERNAME)).size;
        const totalVolume = bets.reduce((s, b) => s + b.amount, 0);
        const largestBet = bets.length > 0 ? Math.max(...bets.map(b => b.amount)) : 0;
        setRoundSummary({
          roundNumber: rn,
          totalPlayers: uniquePlayers,
          totalBets: bets.length,
          totalVolume,
          largestBet,
        });
      });
    } else {
      // ── Mock mode: populate from Game engine ──
      attachToGame();
      if (!game) {
        const onReady = () => attachToGame();
        window.addEventListener('memePump:ready', onReady, { once: true });
        onCleanup(() => window.removeEventListener('memePump:ready', onReady));
      }
    }

    window.addEventListener('memePump:toggleStatsDrawer' as any, handleToggleEvent);
    document.addEventListener('keydown', handleKeyDown);

    onCleanup(() => {
      window.removeEventListener('memePump:toggleStatsDrawer' as any, handleToggleEvent);
      document.removeEventListener('keydown', handleKeyDown);
    });
  });

  const switchView = (mode: ViewMode) => {
    setViewMode(mode);

    // Request fresh data from server for the selected tab
    if (isRealMode) {
      if (mode === 'summary') {
        setSummaryLoading(true);
        gameApi.getPlayerStats();
      } else if (mode === 'my-history') {
        setHistoryLoading(true);
        gameApi.getBetHistory({ limit: 50 });
      } else if (mode === 'top-players') {
        setLeaderboardLoading(true);
        gameApi.getLeaderboard({ type: 'earnings', limit: 10 });
      }
    }

    try {
      const app = (window as any).memePumpApp;
      app?.audio?.play('ui-open', { allowRestart: true });
    } catch {}
  };

  return (
    <>

      <Show when={isOpen()}>
        <div
          class="stats-drawer-overlay"
          onClick={closeDrawer}
        />
      </Show>

      <div
        class="stats-drawer-panel"
        classList={{ 'is-open': isOpen() }}
      >
        <div class="stats-drawer-header">
          <div class="drawer-title">
            <span class="drawer-icon">📊</span>
            <span class="drawer-title-text">Statistics</span>
          </div>
          <button
            class="drawer-close-btn"
            onClick={closeDrawer}
            aria-label="Close stats drawer"
          >
            ✕
          </button>
        </div>

        <div class="stats-drawer-tabs">
          <button
            class="drawer-tab"
            classList={{ active: viewMode() === 'summary' }}
            onClick={() => switchView('summary')}
          >
            <span class="tab-icon">📈</span>
            <span>Summary</span>
          </button>
          <button
            class="drawer-tab"
            classList={{ active: viewMode() === 'my-history' }}
            onClick={() => switchView('my-history')}
          >
            <span class="tab-icon">🎯</span>
            <span>My Bets</span>
          </button>
          <button
            class="drawer-tab"
            classList={{ active: viewMode() === 'round-bets' }}
            onClick={() => switchView('round-bets')}
          >
            <span class="tab-icon">🎲</span>
            <span>Round</span>
          </button>
          <button
            class="drawer-tab"
            classList={{ active: viewMode() === 'top-players' }}
            onClick={() => switchView('top-players')}
          >
            <span class="tab-icon">🏆</span>
            <span>Leaders</span>
          </button>
        </div>

        <div class="stats-drawer-content">
          <Show when={viewMode() === 'summary'}>
            <Show when={!isRealMode || !summaryLoading()} fallback={<SummarySkeleton />}>
              <div class="drawer-section">
                <div class="section-header">
                  <span class="section-icon">💎</span>
                  <h3 class="section-title">Your Performance</h3>
                </div>
                <div class="stats-grid-enhanced">
                  <div class="stat-card-premium">
                    <div class="stats-drawer-stat-icon">💰</div>
                    <div class="stats-drawer-stat-content">
                      <div class="stats-drawer-stat-label">Total Win</div>
                      <div class={`stats-drawer-stat-value stat-xxl ${playerSummary().totalWon >= 0 ? 'stat-success' : 'stat-danger'}`}>
                        {formatCurrency(Math.max(0, playerSummary().totalWon))}
                      </div>
                    </div>
                  </div>

                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Total Bets</div>
                    <div class="stats-drawer-stat-value">{playerSummary().totalBets}</div>
                  </div>
                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Wins</div>
                    <div class="stats-drawer-stat-value stat-success">{playerSummary().totalWins}</div>
                  </div>
                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Win Rate</div>
                    <div class="stats-drawer-stat-value stat-accent">{playerSummary().winRate.toFixed(1)}%</div>
                  </div>
                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Highest Cashout</div>
                    <div class="stats-drawer-stat-value">{playerSummary().highestCashout.toFixed(2)}x</div>
                  </div>
                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Avg Multiplier</div>
                    <div class="stats-drawer-stat-value">{playerSummary().averageMultiplier.toFixed(2)}x</div>
                  </div>
                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Total Wagered</div>
                    <div class="stats-drawer-stat-value">{formatCurrency(playerSummary().totalWagered)}</div>
                  </div>
                </div>
              </div>

              <div class="drawer-section">
                <div class="section-header">
                  <span class="section-icon">🎰</span>
                  <h3 class="section-title">Round #{roundSummary().roundNumber}</h3>
                </div>
                <div class="stats-grid-enhanced">
                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Active Players</div>
                    <div class="stats-drawer-stat-value stat-accent">{roundSummary().totalPlayers}</div>
                  </div>
                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Total Bets</div>
                    <div class="stats-drawer-stat-value">{roundSummary().totalBets}</div>
                  </div>
                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Total Volume</div>
                    <div class="stats-drawer-stat-value">{formatCurrency(roundSummary().totalVolume)}</div>
                  </div>
                  <div class="stat-card-compact">
                    <div class="stats-drawer-stat-label">Largest Bet</div>
                    <div class="stats-drawer-stat-value">{formatCurrency(roundSummary().largestBet)}</div>
                  </div>
                </div>
              </div>
            </Show>
          </Show>

          <Show when={viewMode() === 'my-history'}>
            <div class="drawer-section">
              <div class="section-header">
                <span class="section-icon">📜</span>
                <h3 class="section-title">My Bet History</h3>
                <span class="section-badge">{myBets().length}</span>
              </div>

              <Show when={isRealMode && historyLoading()} fallback={
                <Show when={myBets().length === 0} fallback={
                  <>
                    <div class="bet-table my-history-table">
                      <div class="bet-table-header">
                        <div>Bet</div>
                        <div>Status</div>
                        <div>Multi</div>
                        <div>Win</div>
                      </div>
                      <div class="bet-table-body">
                        <For each={myBets().slice(0, 100)}>
                          {(bet) => <BetRow bet={bet} hidePlayer />}
                        </For>
                      </div>
                    </div>
                  </>
                }>
                  <div class="empty-state">
                    <span class="empty-icon">🎮</span>
                    <p class="empty-text">No bets placed yet</p>
                    <p class="empty-hint">Place a bet to start tracking your stats</p>
                  </div>
                </Show>
              }>
                <>
                  <div class="stats-skeleton-table">
                    <div class="stats-skeleton-row" />
                    <div class="stats-skeleton-row" />
                    <div class="stats-skeleton-row" />
                    <div class="stats-skeleton-row" />
                  </div>
                </>
              </Show>
            </div>
          </Show>

          <Show when={viewMode() === 'round-bets'}>
            <div class="drawer-section">
              <div class="section-header">
                <span class="section-icon">🎲</span>
                <h3 class="section-title">Round #{roundSummary().roundNumber} Bets</h3>
                <span class="section-badge">{roundBets().length}</span>
              </div>

              <Show when={roundBets().length === 0} fallback={
                <>
                  <div class="bet-table">
                    <div class="bet-table-header">
                      <div>Player</div>
                      <div>Bet</div>
                      <div>Status</div>
                      <div>Multi</div>
                      <div>Win</div>
                    </div>
                    <div class="bet-table-body">
                      <For each={roundBets()}>
                        {(bet) => <BetRow bet={bet} />}
                      </For>
                    </div>
                  </div>
                </>
              }>
                <div class="empty-state">
                  <span class="empty-icon">⏳</span>
                  <p class="empty-text">No bets in this round yet</p>
                  <p class="empty-hint">Waiting for players to place bets...</p>
                </div>
              </Show>
            </div>
          </Show>

          <Show when={viewMode() === 'top-players'}>
            <div class="drawer-section">
              <div class="section-header">
                <span class="section-icon">🏆</span>
                <h3 class="section-title">Top Earners</h3>
              </div>
              <Show when={!isRealMode || !leaderboardLoading()} fallback={
                <div class="top-players-skeleton">
                  <div class="stats-skeleton-row" />
                  <div class="stats-skeleton-row" />
                  <div class="stats-skeleton-row" />
                  <div class="stats-skeleton-row" />
                </div>
              }>
                <div class="top-players-list">
                  <For each={topPlayers()}>
                    {(player) => <TopPlayerRow player={player} />}
                  </For>
                </div>
              </Show>
            </div>
          </Show>
        </div>

        <div class="stats-drawer-footer">
          <span class="footer-hint">Press <kbd>Esc</kbd> to close</span>
        </div>
      </div>
    </>
  );
}

function normalizeBetState(raw: string): BetState {
  const value = String(raw || '').toUpperCase();
  if (value === 'WON') return 'won';
  if (value === 'CASHED') return 'cashed';
  if (value === 'LOST') return 'lost';
  if (value === 'PENDING') return 'pending';
  if (value === 'QUEUED') return 'queued';
  if (value === 'ACTIVE') return 'active';
  if (value === 'CANCELED' || value === 'CANCELLED') return 'canceled';
  if (value === 'REFUNDED') return 'refunded';
  return 'unknown';
}

function SummarySkeleton() {
  return (
    <>
      <div class="drawer-section">
        <div class="section-header">
          <span class="section-icon">💎</span>
          <h3 class="section-title">Your Performance</h3>
        </div>
        <div class="stats-grid-enhanced stats-skeleton-grid">
          <div class="stats-skeleton-card stats-skeleton-card-premium" />
          <div class="stats-skeleton-card" />
          <div class="stats-skeleton-card" />
          <div class="stats-skeleton-card" />
          <div class="stats-skeleton-card" />
          <div class="stats-skeleton-card" />
          <div class="stats-skeleton-card" />
        </div>
      </div>
      <div class="drawer-section">
        <div class="section-header">
          <span class="section-icon">🎰</span>
          <h3 class="section-title">Round Stats</h3>
        </div>
        <div class="stats-grid-enhanced stats-skeleton-grid stats-skeleton-grid-round">
          <div class="stats-skeleton-card" />
          <div class="stats-skeleton-card" />
          <div class="stats-skeleton-card" />
          <div class="stats-skeleton-card" />
        </div>
      </div>
    </>
  );
}

function BetRow(props: { bet: TrackedBet; hidePlayer?: boolean }) {
  const bet = () => props.bet;
  const hidePlayer = () => !!props.hidePlayer;

  const stateInfo = () => {
    const s = bet().state;
    switch (s) {
      case 'pending': return { icon: '⏳', label: 'Queue', class: 'status-pending' };
      case 'queued': return { icon: '🕓', label: 'Queued', class: 'status-pending' };
      case 'active': return { icon: '🚀', label: 'Active', class: 'status-active' };
      case 'cashed': return { icon: '✅', label: 'Cash', class: 'status-cashed' };
      case 'won': return { icon: '🎉', label: 'Won', class: 'status-won' };
      case 'lost': return { icon: '💥', label: 'Lost', class: 'status-lost' };
      case 'canceled': return { icon: '↩', label: 'Canceled', class: 'status-canceled' };
      case 'refunded': return { icon: '↺', label: 'Refunded', class: 'status-refunded' };
      default: return { icon: '?', label: 'Unknown', class: 'status-unknown' };
    }
  };

  return (
    <div class={`bet-table-row ${stateInfo().class}`}>
      <Show when={!hidePlayer()} fallback={null}>
        <div class="stats-drawer-col-player">
          <div class="stats-drawer-player-info">
            <div class="stats-drawer-player-avatar">
              <img
                src={avatarSrc(bet().avatar || DEFAULT_AVATAR)}
                alt=""
              />
            </div>
            <span class="stats-drawer-player-name">{bet().username || DEFAULT_USERNAME}</span>
          </div>
        </div>
      </Show>
      <div class="stats-drawer-col-bet">
        <span class="stats-drawer-bet-amount">{formatCurrency(bet().amount)}</span>
      </div>
      <div class="stats-drawer-col-status">
        <span class="stats-drawer-status-badge">
          <span class="stats-drawer-status-icon">{stateInfo().icon}</span>
          <span class="stats-drawer-status-label">{stateInfo().label}</span>
        </span>
      </div>
      <div class="stats-drawer-col-multiplier">
        <Show when={bet().cashoutMultiplier} fallback={<span class="stats-drawer-placeholder">—</span>}>
          <span class="stats-drawer-multiplier-value">{bet().cashoutMultiplier!.toFixed(2)}x</span>
        </Show>
      </div>
      <div class="stats-drawer-col-win">
        <Show when={bet().winAmount} fallback={
          <span class="stats-drawer-placeholder">—</span>
        }>
          <span class="stats-drawer-win-amount">{formatCurrency(bet().winAmount!)}</span>
        </Show>
      </div>
    </div>
  );
}

function TopPlayerRow(props: { player: TopPlayer }) {
  const player = () => props.player;

  return (
    <div class={`top-player-row rank-${player().rank}`}>
      <div class="stats-drawer-player-rank">
        <Show when={player().rank === 1}>🥇</Show>
        <Show when={player().rank === 2}>🥈</Show>
        <Show when={player().rank === 3}>🥉</Show>
        <Show when={player().rank > 3}>
          <span class="stats-drawer-rank-number">#{player().rank}</span>
        </Show>
      </div>
      <div class="stats-drawer-player-avatar-large">
        <img
          src={avatarSrc(player().avatar)}
          alt={player().username}
        />
      </div>
      <div class="stats-drawer-player-details">
        <div class="stats-drawer-player-name-large">{player().username}</div>
        <div class="stats-drawer-player-value-large">{formatCurrency(player().value)}</div>
      </div>
    </div>
  );
}
